import type { FC, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import type { IModalText, IOnSubmit, ISharing } from "app/components/sharingAssetModal/types";
import { ESharingState } from "app/components/sharingAssetModal/types";
import type { IProModal } from "pro-features/components/Modals/src/ProModal";
import { MainModal } from "app/components/sharingAssetModal/components/MainModal";
import { ExitConfirmModal } from "app/components/sharingAssetModal/components/ExitConfirmModal";
import { useDispatch } from "react-redux";
import { showSuccessToast } from "app/actions/toast_actions";
import { i18nFilter } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { getInitialSharingStateHof, getTrackingMD } from "app/components/sharingAssetModal/components/utils";
import { noop } from "lodash";

interface ISharingAssetModal {
    initialSharingState?: ESharingState;
    sharedWithUsers?: Array<number>;
    onSubmit: IOnSubmit;
    onBannerClick?: (ISharing) => void;
    modalText: IModalText;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    disabledSharingStates?: Array<ESharingState>;
    hideSharingStates?: Array<ESharingState>;
    customTrackingAttributesOnShare?: Record<string, string | number | boolean>;
    CustomNameComponent?: ReactNode;
    showNotificationCheckboxInMainModal?: boolean;
    hideCancelButton?: boolean;
    dismissOnBlur?: boolean;
    checkboxText?: (sharingState) => string;
}

const { PRIVATE, SUBSET_OF_USERS } = ESharingState;

const i18n = i18nFilter();

export const SharingAssetModal: FC<ISharingAssetModal> = (props) => {
    const {
        initialSharingState = PRIVATE,
        modalText,
        onSubmit,
        onBannerClick,
        sharedWithUsers: initialSharedWithUsers = [],
        isOpen,
        setIsOpen,
        disabledSharingStates = [],
        hideSharingStates = [],
        customTrackingAttributesOnShare = {},
        CustomNameComponent,
        showNotificationCheckboxInMainModal,
        hideCancelButton,
        dismissOnBlur,
        checkboxText,
    } = props;

    const INITIAL_SHARING_STATE = useMemo(
        () => getInitialSharingStateHof(initialSharingState, initialSharedWithUsers, modalText)(),
        [initialSharedWithUsers, initialSharingState, modalText],
    );

    const dispatch = useDispatch();
    const [isMainModalOpen, setIsMainModalOpen] = useState<boolean>(isOpen);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isExitModalOpen, setIsExitModalOpen] = useState<boolean>(false);
    const [sharing, setSharing] = useState<ISharing>(INITIAL_SHARING_STATE);
    const resetSharingState = () => {
        setSharing(INITIAL_SHARING_STATE);
    };
    useEffect(() => {
        resetSharingState();
        if (isOpen) {
            setIsMainModalOpen(true);
            setIsExitModalOpen(false);
        } else {
            setIsMainModalOpen(false);
            setIsExitModalOpen(false);
        }
    }, [isOpen]);

    const { sharingState, sharedWithUsers, notification } = sharing;
    const closeModals = () => {
        setIsOpen(false);
    };

    const displayExitConfirmModal = () => {
        setIsExitModalOpen(true);
        setIsMainModalOpen(false);
    };

    const displayMainModal = () => {
        setIsMainModalOpen(true);
        setIsExitModalOpen(false);
    };

    const onCloseClickMiddleWare = () => {
        if (sharingState === SUBSET_OF_USERS) {
            if (sharedWithUsers.length > 0) {
                allTrackers.trackEvent("Pop Up", "open", "Cancel restricted");
                displayExitConfirmModal();
                return;
            }
        }
        allTrackers.trackEvent("Pop Up", "close", "Share custom industry");
        closeModals();
    };

    const defaultModalProps: IProModal = {
        showCloseIcon: false,
        customStyles: { content: { padding: 0, width: "auto" } },
    };

    const mainModalProps: IProModal = {
        ...defaultModalProps,
        onCloseClick: dismissOnBlur ? noop : onCloseClickMiddleWare,
        isOpen: isMainModalOpen,
    };

    const exitConfirmModalProps: IProModal = {
        ...defaultModalProps,
        isOpen: isExitModalOpen,
    };

    const onShare = () => {
        setIsLoading(true);
        const shouldNotify = notification.shouldNotify && sharingState === SUBSET_OF_USERS;
        const defaultTrackingName = `${getTrackingMD(shouldNotify).previousState[initialSharingState]}/${
            getTrackingMD(shouldNotify).newState[sharingState]
        }`;
        const trackingName =
            sharingState === SUBSET_OF_USERS ? `${defaultTrackingName}/${sharedWithUsers.length}` : defaultTrackingName;

        allTrackers.dispatchTrackEvent("Pop Up", "close", trackingName, customTrackingAttributesOnShare);
        const result = onSubmit(sharing);
        if (!result) {
            setIsLoading(false);
            return;
        }
        result.then(() => {
            closeModals();
        });
        result.catch(() => {
            dispatch(showSuccessToast(i18n("sharing.asset.modal.operation.failed"), Date.now(), 7_000));
        });
        result.finally(() => {
            setIsLoading(false);
        });
    };

    return (
        <>
            <MainModal
                modalProps={mainModalProps}
                onShare={onShare}
                onBannerClick={onBannerClick}
                text={modalText}
                isLoading={isLoading}
                onCancel={onCloseClickMiddleWare}
                setSharing={setSharing}
                sharing={sharing}
                disabledSharingStates={disabledSharingStates}
                hideSharingStates={hideSharingStates}
                CustomNameComponent={CustomNameComponent}
                showNotificationCheckboxInMainModal={showNotificationCheckboxInMainModal}
                hideCancelButton={hideCancelButton}
                checkboxText={checkboxText ? checkboxText(sharing.sharingState) : null}
            />
            <ExitConfirmModal
                modalProps={exitConfirmModalProps}
                exitConfirmProps={{ displayMainModal, onClose: closeModals }}
            />
        </>
    );
};
